<template>
  <div class="kiosk-welcome w-full sm:w-2/3 xl:w-2/5">
    <div class="vx-card kiosk-card p-6 sm:p-10 text-center">
      <!-- Welcome heading -->
      <h1 class="text-2xl md:text-4xl font-bold text-dark mb-3 leading-tight">
        Welcome to {{ brandName }}
      </h1>
      <p class="text-base text-content mb-8 leading-relaxed">
        We're excited to see you. Join the queue and we'll notify you when it's your turn.
      </p>

      <!-- Stats cards -->
      <div class="flex justify-center mb-8">
        <div class="kiosk-stat-card flex flex-col items-center justify-center px-8 py-5">
          <feather-icon icon="UsersIcon" svgClasses="h-6 w-6 mb-2 text-content" />
          <span class="text-3xl font-bold text-dark">{{ peopleWaiting }}</span>
          <span class="text-xs text-content font-medium uppercase tracking-wider mt-1">People waiting</span>
        </div>
      </div>

      <!-- Join button -->
      <vs-button
        v-if="openDay"
        :style="`background: ${accentColour} !important; color: ${accentContrastTextColor} !important`"
        class="kiosk-join-btn w-full shadow-md mb-8"
        @click="$emit('join')"
      >
        Join waitlist
      </vs-button>

      <p v-if="!openDay" class="text-content mb-8">
        The queue is currently closed. Please check back later.
      </p>

      <!-- Divider -->
      <vs-divider />

      <!-- QR code section -->
      <div class="mt-6">
        <div class="flex items-center justify-center mb-4">
          <feather-icon icon="SmartphoneIcon" svgClasses="h-5 w-5 mr-2 text-content" />
          <span class="text-sm font-semibold text-dark">Continue on your phone</span>
        </div>

        <div class="kiosk-qr-frame mx-auto mb-3">
          <qrcode-vue
            :value="waitlistFormUrl"
            :size="160"
            level="H"
            render-as="canvas"
            class="flex justify-center"
          />
        </div>

        <p class="text-xs text-content">Scan to open waitlist</p>
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';
import { getContrastColor } from '@/utils/colorUtils';

export default {
  name: 'KioskWelcome',
  components: {
    QrcodeVue,
  },
  props: {
    brandName: {
      type: String,
      required: true,
    },
    accentColour: {
      type: String,
      required: true,
    },
    peopleWaiting: {
      type: Number,
      default: 0,
    },
    openDay: {
      type: Boolean,
      default: true,
    },
    waitlistFormUrl: {
      type: String,
      required: true,
    },
  },
  computed: {
    accentContrastTextColor() {
      return this.accentColour ? getContrastColor(this.accentColour) : '#ffffff';
    },
  },
};
</script>

<style lang="scss" scoped>
.kiosk-welcome {
  animation: slideUp 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.kiosk-card {
  border-radius: 16px;
  position: relative;
  overflow: hidden;

  // Subtle gradient accent in background
  &::before {
    content: '';
    position: absolute;
    top: -60px;
    left: -60px;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(93, 38, 175, 0.08), rgba(93, 38, 175, 0.02));
    border-radius: 50%;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.kiosk-stat-card {
  background: #f8f8f8;
  border: 1px solid #e4e7eb;
  border-radius: 12px;
  min-width: 160px;
}

.kiosk-join-btn {
  transition: all 0.2s ease !important;

  &:hover {
    border: 1px solid v-bind('accentColour') !important;
    box-shadow: 0 8px 25px -8px v-bind('accentColour') !important;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px -6px v-bind('accentColour') !important;
  }
}

.kiosk-qr-frame {
  background: #fff;
  border: 1px solid #e4e7eb;
  border-radius: 12px;
  padding: 16px;
  display: inline-block;
}

@keyframes slideUp {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
